"use client";

import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Lock, Eye, EyeOff, User, Phone, ArrowLeft } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { TermsModal } from "@/components/ui/terms-modal";
import { TestimonialsPanel } from "@/components/testimonials-panel";
import { goToOnboarding } from "@/lib/onboarding-redirect";

// Declaração global para Facebook Pixel
declare global {
  interface Window {
    fbq: any;
  }
}

const referralOptions = [
  { value: "google", label: "Google" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "twitter", label: "Twitter/X" },
  { value: "youtube", label: "YouTube" },
  { value: "friend", label: "Indicação de amigo" },
  { value: "email", label: "Email marketing" },
  { value: "advertisement", label: "Anúncio" },
  { value: "other", label: "Outro" },
];

function RegisterForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    referralSource: "",
    referralSourceOther: "",
    acceptTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [utmParams, setUtmParams] = useState<string>("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);
  const [hasSpacesInPassword, setHasSpacesInPassword] = useState(false);
  const [hasSpacesInConfirmPassword, setHasSpacesInConfirmPassword] = useState(false);
  const searchParams = useSearchParams();

  // Capturar parâmetros UTM da URL
  useEffect(() => {
    const utmParamsArray: string[] = [];
    
    // Lista de parâmetros UTM que queremos capturar
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    
    utmKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        utmParamsArray.push(`${key}=${value}`);
      }
    });
    
    if (utmParamsArray.length > 0) {
      setUtmParams(utmParamsArray.join('&'));
    }
  }, [searchParams]);

  // Função para criar URL com UTMs preservados
  const createUrlWithUtms = (path: string) => {
    if (utmParams) {
      return `${path}?${utmParams}`;
    }
    return path;
  };

  // Verificar espaços nas senhas quando elas mudam
  useEffect(() => {
    if (formData.password && formData.password.includes(' ')) {
      setHasSpacesInPassword(true);
    } else {
      setHasSpacesInPassword(false);
    }
  }, [formData.password]);

  useEffect(() => {
    if (formData.confirmPassword && formData.confirmPassword.includes(' ')) {
      setHasSpacesInConfirmPassword(true);
    } else {
      setHasSpacesInConfirmPassword(false);
    }
  }, [formData.confirmPassword]);

  // Máscara para telefone
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }
    return numbers.slice(0, 11).replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
    // Limpar erro geral se não houver mais espaços
    if (errors.general && errors.general.includes("espaços")) {
      const passwordValue = field === 'password' ? (typeof value === 'string' ? value : '') : formData.password;
      const confirmValue = field === 'confirmPassword' ? (typeof value === 'string' ? value : '') : formData.confirmPassword;
      if (!passwordValue.includes(' ') && !confirmValue.includes(' ')) {
        setErrors(prev => ({ ...prev, general: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Telefone é obrigatório";
    } else if (formData.phone.replace(/\D/g, "").length < 10) {
      newErrors.phone = "Telefone inválido";
    }

    if (!formData.password) {
      newErrors.password = "Senha é obrigatória";
    } else if (formData.password.includes(' ')) {
      newErrors.password = "A senha não pode conter espaços";
    } else if (formData.password.length < 6) {
      newErrors.password = "Senha deve ter pelo menos 6 caracteres";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirme sua senha";
    } else if (formData.confirmPassword.includes(' ')) {
      newErrors.confirmPassword = "A confirmação de senha não pode conter espaços";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Senhas não coincidem";
    }

    if (!formData.referralSource) {
      newErrors.referralSource = "Selecione onde nos conheceu";
    }

    if (formData.referralSource === "other" && !formData.referralSourceOther.trim()) {
      newErrors.referralSourceOther = "Digite onde nos conheceu";
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = "Você precisa aceitar os termos de uso para continuar";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevenir submissão se houver espaços nas senhas
    if (formData.password.includes(' ') || formData.confirmPassword.includes(' ')) {
      setErrors({ general: "Por favor, remova os espaços das senhas antes de continuar" });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email.toLowerCase().trim(),
          phone: formData.phone,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          referralSource: formData.referralSource,
          referralSourceOther: formData.referralSourceOther,
          utmParams: utmParams,
          acceptTerms: formData.acceptTerms,
          ref: searchParams.get("ref") || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao registrar usuário');
      }

      // Automatically sign in the user after successful registration
      const signInResult = await signIn('credentials', {
        email: formData.email,
        password: formData.password,
        redirect: false,
      });

      if (signInResult?.error) {
        throw new Error('Registro realizado com sucesso, mas erro ao fazer login automático');
      }

      // Redireciona pro onboarding (serviço externo, com fallback interno)
      await goToOnboarding();
    } catch (error: any) {
      setErrors({ general: error.message || 'Erro ao registrar usuário' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 overflow-hidden">
      {/* Left Panel - Register Section */}
      <div className="w-full lg:w-2/5 bg-white/80 backdrop-blur-sm flex items-start lg:items-center justify-center p-6 sm:p-8 lg:p-12 h-screen overflow-y-auto">
        <div className="w-full max-w-md space-y-8 py-8 lg:py-0">
          <div>
            <div className="w-full mb-4 mt-48">
                  <Image
                    src="/logo-nova-vagaautomatica.png"
                    alt="Vaga Automática"
                    width={200}
                    height={100}
                    className="h-[160px] w-auto object-contain ml-[-20px] mb-[-40px]"
                    priority
                  />              
              </div>
            <h1 className="text-3xl font-bold text-gray-900">Crie sua conta</h1>
            <p className="text-gray-600">Automatize suas candidaturas de emprego!</p> 
          </div>

          {/* Register Form */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Registro</h2>
              <p className="text-sm text-gray-600">
                Preencha seus dados para criar sua conta
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
                {errors.general && (
                  <div className="text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-lg p-3">
                    {errors.general}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                    Nome completo
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Seu nome completo"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className="pl-10 h-10"
                      required
                    />
                  </div>
                  {errors.name && (
                    <p className="text-red-500 text-xs">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    E-mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value.toLowerCase())}
                      className="pl-10 h-10"
                      required
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs">{errors.email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    Telefone
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="pl-10 h-10"
                      required
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-xs">{errors.phone}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Sua senha"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      className={`pl-10 pr-10 h-10 ${hasSpacesInPassword ? 'border-amber-500 focus:border-amber-500 focus:ring-amber-500' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {hasSpacesInPassword && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-semibold mb-1">Atenção: Espaços detectados na senha</p>
                        <p className="text-xs leading-relaxed">
                          A senha não pode conter espaços. Por favor, remova todos os espaços antes de continuar.
                          <span className="font-medium block mt-1">Dica: Digite a senha sem espaços extras.</span>
                        </p>
                      </div>
                    </div>
                  )}
                  {errors.password && !hasSpacesInPassword && (
                    <p className="text-red-500 text-xs">{errors.password}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    Confirmar senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirme sua senha"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                      className={`pl-10 pr-10 h-10 ${hasSpacesInConfirmPassword ? 'border-amber-500 focus:border-amber-500 focus:ring-amber-500' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {hasSpacesInConfirmPassword && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-semibold mb-1">Atenção: Espaços detectados na confirmação</p>
                        <p className="text-xs leading-relaxed">
                          A confirmação de senha não pode conter espaços. Por favor, remova todos os espaços antes de continuar.
                          <span className="font-medium block mt-1">Dica: Digite a senha sem espaços extras.</span>
                        </p>
                      </div>
                    </div>
                  )}
                  {errors.confirmPassword && !hasSpacesInConfirmPassword && (
                    <p className="text-red-500 text-xs">{errors.confirmPassword}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referralSource" className="text-sm font-medium text-gray-700">
                    Onde nos conheceu?
                  </Label>
                  <Select
                    value={formData.referralSource}
                    onValueChange={(value) => handleInputChange("referralSource", value)}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                    <SelectContent>
                      {referralOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.referralSource && (
                    <p className="text-red-500 text-xs">{errors.referralSource}</p>
                  )}
                </div>

                {formData.referralSource === "other" && (
                  <div className="space-y-2">
                    <Label htmlFor="referralSourceOther" className="text-sm font-medium text-gray-700">
                      Digite onde nos conheceu
                    </Label>
                    <Input
                      id="referralSourceOther"
                      type="text"
                      placeholder="Ex: Evento, Podcast, etc."
                      value={formData.referralSourceOther}
                      onChange={(e) => handleInputChange("referralSourceOther", e.target.value)}
                      className="h-10"
                      required
                    />
                    {errors.referralSourceOther && (
                      <p className="text-red-500 text-xs">{errors.referralSourceOther}</p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="accept-terms"
                      checked={formData.acceptTerms}
                      onCheckedChange={(checked) => handleInputChange("acceptTerms", checked as boolean)}
                    />
                    <div className="space-y-1">
                      <Label htmlFor="accept-terms" className="text-sm font-medium leading-tight ml-1">
                        {" "} Li e concordo com os{" "}
                        <Link
                          href="https://vagaautomatica.com/termos"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-blue-800 underline"
                        >
                          Termos de Uso
                        </Link>{" "}
                      </Label>
                      <p className="text-xs text-gray-500">
                        É necessário aceitar os termos para criar sua conta no VagaAutomatica.
                      </p>
                    </div>
                  </div>
                  {errors.acceptTerms && (
                    <p className="text-red-500 text-xs">{errors.acceptTerms}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="cursor-pointer w-full bg-primary from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-blue-500/30 transition-all duration-200 h-11"
                  disabled={isLoading || !formData.acceptTerms || hasSpacesInPassword || hasSpacesInConfirmPassword}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Processando...
                    </>
                  ) : (
                    "Criar conta"
                  )}
                </Button>

                <div className="text-center text-sm text-gray-600 pt-2">
                  Já tem uma conta?{" "}
                  <Link 
                    href={createUrlWithUtms("/")} 
                    className="text-primary hover:text-blue-700 hover:underline font-semibold transition-colors"
                  >
                    Faça login
                  </Link>
                </div>
              </form>
          </div>
        </div>
      </div>

      {/* Right Panel - Promotional Section */}
      <TestimonialsPanel />
    </div>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Carregando...</p>
        </div>
      </div>
    }>
      <RegisterForm />
    </Suspense>
  );
}
